import { computed, ref, watch } from 'vue';
import { DEFAULT_PAST_INPUT_TIME, MAX_SEARCH_INPUT_NUMBER_VALUE, TIME_UNIT } from '@/common/utils';
import { useInternational } from '@/common/locale';
import type { SelectOption, TimePeriodData } from '@/types/common.types';
import { getOnlyNumberString } from '@/common/utils/string.utils';
import { useRoute } from 'vue-router';
import { webStorageController } from '@/common/utils/browser';
import { isEqual, isNil } from 'lodash-es';
import { getCurrentTimeRange } from '@/common/components/molecules/timeController/utils';
import {
  GLOBAL_TIME_LIVE_FORMAT,
  DEFAULT_PERIOD_ITEMS_ALL,
} from '@/common/components/molecules/timeController/constants';
import { getTimePeriodByRange } from '@/common/utils/core/time/stringify';

import dayjs from 'dayjs';
import {
  getLocaleKey,
  getPeriodToSeconds,
  getTzTimeNow,
  secondsToPeriod,
} from '@/common/utils/commonUtils';
import { useDateTimePicker } from '../dateTimePicker/dateTimePicker.composables';

export type RangeDateTimePickerShortCut = (typeof DEFAULT_PERIOD_ITEMS_ALL)[number]['value'];

type HistoryData = {
  // ex) YYYY.MM.DD ~ YYYY.MM.DD
  label: string;
  // ex) p10s, p10m, p10h, p10d, YYYY.MM.DD ~ YYYY.MM.DD
  value: string;
  // ex) YYYY.MM.DD
  from: string;
  // ex) YYYY.MM.DD
  to: string;
  // 기간을 초로 환산한 값
  rangeInSec: number;
};

type FromString = string;
type ToString = string;

export interface Props {
  data: [FromString, ToString];
  // min range time limit (seconds)
  minRangeInSec?: number | undefined;
  // max range time limit (seconds)
  maxRangeInSec?: number | undefined;
  // 최소 선택 interval (ex. 1시간 일 경우, 1시간으로 딱 나눠 떨어지는 기간만 선택 가능)
  minimumIntervalInSec?: number | undefined;
  // hh:mm:ss, hh:mm:00, hh:00:00 ..etc
  timeFormat?: string;
  timeUnit?: 'day' | 'sec';
  clearable?: boolean;
  quickDateList?: RangeDateTimePickerShortCut[];
  quickCustomDateList?: TimePeriodData[];
  allowFutureDates?: boolean;
  id?: string;
  disabled?: boolean;
  isError?: boolean;
  errorMessage?: string;
  placeholder?: string;
  hidePrefixIcon?: boolean;
  hideInput?: boolean;
  /** 달력 아이콘 대신 기간 태그 표시 (예: 1d, 7d) */
  showPeriodTag?: boolean;
  disableRecentHistory?: boolean;
}

export interface Emit {
  (e: 'update:data', value: [FromString, ToString]): void;
}

export const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();

  const {
    timeCalendarRef,
    dateTimePickerInputRef,
    dateTimePickerPopupRef,
    isShowQuickDatePopup,
    popupWrapperStyle,
    showCalendar,
    closeCalendar,
    openCalendar,
    closeAll,
    togglePopup,
  } = useDateTimePicker(() => isNil(props.quickDateList) || props.quickDateList.length > 0);

  const readonlyTimes = computed<{
    hour: boolean;
    minute: boolean;
    second: boolean;
  }>(() => {
    const result = {
      hour: false,
      minute: false,
      second: false,
    };

    const timeFormat = props?.timeFormat;
    if (timeFormat) {
      const [hour, minute, second] = timeFormat.split(':');
      result.hour = !!(hour && hour?.toLowerCase() !== 'hh');
      result.minute = !!(minute && minute !== 'mm');
      result.second = !!(second && second !== 'ss');
    }

    return result;
  });

  /**
   * 선택된 날짜 시간
   */
  const dateTimePeriod = computed({
    get: () => props.data,
    set: (value) => emit('update:data', value),
  });

  const selectedDateString = ref('');

  watch(
    dateTimePeriod,
    () => {
      if (dateTimePeriod.value[0] && dateTimePeriod.value[1]) {
        if (props.timeUnit === 'day') {
          const [onlyDate1] = dateTimePeriod.value[0].split(' ');
          const [onlyDate2] = dateTimePeriod.value[1].split(' ');
          selectedDateString.value = `${onlyDate1} ~ ${onlyDate2}`;
        } else {
          selectedDateString.value = `${dateTimePeriod.value[0]} ~ ${dateTimePeriod.value[1]}`;
        }
      } else {
        selectedDateString.value = '';
      }
    },
    {
      deep: true,
      immediate: true,
    },
  );

  watch(selectedDateString, (newValue) => {
    if (!newValue) {
      emit('update:data', ['', '']);
    }
  });

  /**
   * 기간 태그 라벨 계산 (예: "1d", "7d", "2h")
   */
  const formatShortPeriodLabel = (period: string): string => {
    if (!period) {
      return '';
    }

    // "today"도 "1d"로 표시
    if (period === 'today') {
      return '1d';
    }

    const matchedPeriod = props.quickCustomDateList?.find((item) => item.value === period);
    if (matchedPeriod?.shortName) {
      return matchedPeriod.shortName;
    }

    // p1d, p7d 등에서 숫자+단위만 추출
    const normalized = period
      .replace(/^custom_/, '')
      .replace(/^p/, '')
      .trim();
    const tokens = normalized.split(/\s+/).filter(Boolean);
    const firstNonZeroToken = tokens.find((token) => !token.startsWith('0')) ?? tokens[0];

    return firstNonZeroToken ?? period;
  };

  const periodTagLabel = computed<string>(() => {
    const [fromTime, toTime] = dateTimePeriod.value;
    if (!fromTime || !toTime) {
      return '';
    }

    const period = getTimePeriodByRange(fromTime, toTime);
    return formatShortPeriodLabel(period);
  });

  /**
   * 검색 이력 저장 / 조회
   */
  const MAXIMUM_HISTORY_SIZE = 5;

  const recentlySearches = ref<
    {
      id: number;
      value: HistoryData;
    }[]
  >([]);

  const route = useRoute();
  const storageKey = ref<string>('');
  watch(
    () => route,
    () => {
      storageKey.value = `rangeDateTimePicker_recently_searches_${route.path}`;
      if (props.id) {
        storageKey.value += `_${props.id}`;
      }

      recentlySearches.value = JSON.parse(
        webStorageController.getItem({
          type: 'local',
          key: storageKey.value,
        }) ?? '[]',
      )?.filter(({ value }) => {
        const minRange = props?.minRangeInSec;
        const maxRange = props?.maxRangeInSec;
        const minimumInterval = props?.minimumIntervalInSec;

        return (
          (minRange ? value.rangeInSec >= minRange : true) &&
          (maxRange ? value.rangeInSec <= maxRange : true) &&
          (minimumInterval ? value.rangeInSec % minimumInterval === 0 : true)
        );
      });
    },
    {
      immediate: true,
      deep: true,
    },
  );

  const saveRecentlySearch = (historyData: HistoryData) => {
    if (props.disableRecentHistory || !storageKey.value) {
      return;
    }

    const duplicatedItemIndex = recentlySearches.value.findIndex(({ value: listItem }) =>
      isEqual(listItem.value, historyData.value),
    );

    if (duplicatedItemIndex > -1) {
      recentlySearches.value.splice(duplicatedItemIndex, 1);
    }

    recentlySearches.value.unshift({
      id: Date.now(),
      value: historyData,
    });

    if (recentlySearches.value.length > MAXIMUM_HISTORY_SIZE) {
      recentlySearches.value.pop();
    }

    webStorageController.setItem({
      type: 'local',
      key: storageKey.value,
      value: JSON.stringify(recentlySearches.value),
    });
  };

  const deleteRecentlySearch = (index: number) => {
    if (props.disableRecentHistory || !storageKey.value) {
      return;
    }

    recentlySearches.value.splice(index, 1);

    webStorageController.setItem({
      type: 'local',
      key: storageKey.value,
      value: JSON.stringify(recentlySearches.value),
    });
  };

  /**
   * 과거 입력 시간
   */
  const pastInputTime = ref<string>(DEFAULT_PAST_INPUT_TIME.value);
  const pastInputError = ref<string>('');
  const pastInputUnit = ref<string>(DEFAULT_PAST_INPUT_TIME.unit);

  const timeUnitItems: SelectOption<string>[] = [
    {
      name: t('WORD.TIME_PERIOD_ITEMS.PAST_DYNAMIC_UNIT', {
        unit: t('DESC.UI.MONTH'),
      }),
      value: TIME_UNIT.month,
    },
    {
      name: t('WORD.TIME_PERIOD_ITEMS.PAST_DYNAMIC_UNIT', { unit: t('DESC.UI.DAY') }),
      value: TIME_UNIT.day,
    },
    {
      name: t('WORD.TIME_PERIOD_ITEMS.PAST_DYNAMIC_UNIT', { unit: t('DESC.UI.HOUR') }),
      value: TIME_UNIT.hour,
    },
    {
      name: t('WORD.TIME_PERIOD_ITEMS.PAST_DYNAMIC_UNIT', { unit: t('DESC.UI.MINUTE') }),
      value: TIME_UNIT.minute,
    },
    {
      name: t('WORD.TIME_PERIOD_ITEMS.PAST_DYNAMIC_UNIT', { unit: t('DESC.UI.SECOND') }),
      value: TIME_UNIT.second,
    },
  ];

  const filteredTimeUnitItems = computed<SelectOption<string>[]>(() => {
    return timeUnitItems.filter((item, index) => {
      const minRangeUnit =
        props?.minRangeInSec && props.minRangeInSec > 0
          ? secondsToPeriod(props.minRangeInSec)
          : undefined;
      const minRangeUnitIndex = timeUnitItems.findIndex(
        (unit) => unit.value === minRangeUnit?.unit,
      );

      const maxRangeUnit =
        props?.maxRangeInSec && props.maxRangeInSec > 0
          ? secondsToPeriod(props.maxRangeInSec)
          : undefined;
      const maxRangeUnitIndex = timeUnitItems.findIndex(
        (unit) => unit.value === maxRangeUnit?.unit,
      );

      const minimumIntervalUnit =
        props?.minimumIntervalInSec && props.minimumIntervalInSec > 0
          ? secondsToPeriod(props.minimumIntervalInSec)
          : undefined;
      const intervalUnitIndex = timeUnitItems.findIndex(
        (unit) => unit.value === minimumIntervalUnit?.unit,
      );

      return (
        (minRangeUnitIndex > 0 ? index <= minRangeUnitIndex : true) &&
        (maxRangeUnitIndex > 0 ? index >= maxRangeUnitIndex : true) &&
        (intervalUnitIndex > 0 ? index <= intervalUnitIndex : true)
      );
    });
  });

  watch(
    filteredTimeUnitItems,
    (newTimeUnitList) => {
      const selectedItem = pastInputUnit.value;
      const notExistInList = !newTimeUnitList.find((item) => item.value === selectedItem);
      const existDefaultSelectedInList = newTimeUnitList.find(
        (item) => item.value === DEFAULT_PAST_INPUT_TIME.unit,
      );

      if (!selectedItem && existDefaultSelectedInList) {
        pastInputUnit.value = DEFAULT_PAST_INPUT_TIME.unit;
      } else if (notExistInList) {
        pastInputUnit.value = newTimeUnitList?.[newTimeUnitList.length - 1]?.value ?? '';
      }
    },
    {
      deep: true,
      immediate: true,
    },
  );

  watch(
    () => props.minimumIntervalInSec,
    (interval: number | undefined) => {
      if (interval) {
        pastInputTime.value = `${secondsToPeriod(interval).value}`;
      }
    },
    {
      immediate: true,
    },
  );

  watch(
    () => props.minRangeInSec,
    (newMinRangeInSec) => {
      if (newMinRangeInSec && newMinRangeInSec > 0) {
        const { value } = secondsToPeriod(newMinRangeInSec);
        pastInputTime.value = `${value}`;
      }
    },
    {
      immediate: true,
    },
  );

  const filteredQuickPeriods = computed<
    {
      name: string;
      value: string;
      shortName: string;
    }[]
  >(() => {
    const { quickDateList, quickCustomDateList } = props;
    if (quickDateList) {
      return DEFAULT_PERIOD_ITEMS_ALL.filter((item) => quickDateList.includes(item.value))?.map(
        ({ name, value, shortName }) => ({
          name: `${t(`WORD.TIME_PERIOD_ITEMS.${getLocaleKey(name)}`)}`,
          value,
          shortName,
        }),
      );
    }

    if (quickCustomDateList) {
      return quickCustomDateList.map((item) => ({
        name: `${t(`WORD.TIME_PERIOD_ITEMS.${getLocaleKey(item.name)}`)}`,
        value: item.value,
        shortName: item.shortName,
        direction: item?.direction ?? 'past',
      }));
    }

    return DEFAULT_PERIOD_ITEMS_ALL.filter((item) => {
      const itemSeconds = getPeriodToSeconds(item.shortName);
      const minRange = props?.minRangeInSec;
      const maxRange = props?.maxRangeInSec;
      const minimumInterval = props?.minimumIntervalInSec;

      return (
        (minRange ? itemSeconds >= minRange : true) &&
        (maxRange ? itemSeconds <= maxRange : true) &&
        (minimumInterval ? itemSeconds % minimumInterval === 0 : true)
      );
    })?.map(({ name, value, shortName }) => ({
      name: `${t(`WORD.TIME_PERIOD_ITEMS.${getLocaleKey(name)}`)}`,
      value,
      shortName,
    }));
  });

  const onInputValidation = (inputTime: string | number): void => {
    if (!inputTime) {
      pastInputError.value = t('MESSAGE.ENTER_THE_TIME');
      return;
    }

    const rangeInSec = getPeriodToSeconds(`${inputTime}${pastInputUnit.value}`);

    if (props.maxRangeInSec && props.maxRangeInSec > 0 && rangeInSec > props.maxRangeInSec) {
      const period = secondsToPeriod(props.maxRangeInSec);
      pastInputError.value = t('MESSAGE.VALIDATION_GUIDE', {
        value: t(`WORD.${period.locale}`, { value: period.value }),
      });
      return;
    }

    if (props.minRangeInSec && props.minRangeInSec > 0 && rangeInSec < props.minRangeInSec) {
      const period = secondsToPeriod(props.minRangeInSec);
      pastInputError.value = t('MESSAGE.VALIDATION_GUIDE_MIN', {
        value: t(`WORD.${period.locale}`, { value: period.value }),
      });
      return;
    }

    if (
      props.minimumIntervalInSec &&
      props.minimumIntervalInSec > 0 &&
      rangeInSec % props.minimumIntervalInSec !== 0
    ) {
      const period = secondsToPeriod(props.minimumIntervalInSec);
      pastInputError.value = t('MESSAGE.VALIDATION_INTERVAL', {
        value: t(`WORD.${period.locale}`, { value: period.value }),
      });
      return;
    }

    pastInputError.value = '';
    pastInputTime.value = getOnlyNumberString(`${inputTime}`, {
      min: 1,
      max: MAX_SEARCH_INPUT_NUMBER_VALUE,
    });
  };

  watch(pastInputUnit, () => {
    onInputValidation(pastInputTime.value);
  });

  /**
   * 기간 선택 (과거 입력, 이력 선택, 빠른 선택, 캘린더)
   */
  const getRangeInSec = (from: string, to: string) => {
    return dayjs(to).diff(dayjs(from), 'second');
  };

  const getFixedTimeByTimeFormat = () => {
    const result = {
      hour: -1,
      minute: -1,
      second: -1,
    };

    if (props.timeFormat) {
      const [hour = '-1', minute = '-1', second = '-1'] = props.timeFormat.split(':');

      return {
        hour: !Number.isNaN(+hour) && +hour > -1 && +hour < 24 ? +hour : -1,
        minute: !Number.isNaN(+minute) && +minute > -1 && +minute < 60 ? +minute : -1,
        second: !Number.isNaN(+second) && +second > -1 && +second < 60 ? +second : -1,
      };
    }

    return result;
  };

  const applyRange = (from: string, to: string) => {
    let label = `${from} ~ ${to}`;

    if (props.timeUnit === 'day') {
      const [fromDay] = from.split(' ');
      const [toDay] = to.split(' ');

      label = `${fromDay} ~ ${toDay}`;
    }

    saveRecentlySearch({
      label,
      value: `${from} ~ ${to}`,
      from,
      to,
      rangeInSec: getRangeInSec(from, to),
    });

    dateTimePeriod.value = [from, to];

    closeAll();
  };

  const onClickInputTimeOk = (inputTime: string, inputUnit: string) => {
    let time = inputTime;
    let unit = inputUnit;

    if (inputUnit === TIME_UNIT.month) {
      unit = 'd';

      const now = getTzTimeNow();
      const monthAgo = now.subtract(+time, 'month');
      time = `${now.diff(monthAgo, 'day')}`;
    }

    const { fromTime, toTime } = getCurrentTimeRange({
      period: `${time}${unit}`,
      timeFormat: GLOBAL_TIME_LIVE_FORMAT,
      fixedTimes: getFixedTimeByTimeFormat(),
    });

    applyRange(fromTime, toTime);
  };

  const onClickRecentlySearch = (selectedHistory: HistoryData) => {
    if (selectedHistory.value?.includes('~')) {
      dateTimePeriod.value = [selectedHistory.from, selectedHistory.to];
    } else {
      const { fromTime, toTime } = getCurrentTimeRange({
        period: selectedHistory.value,
        timeFormat: GLOBAL_TIME_LIVE_FORMAT,
        fixedTimes: getFixedTimeByTimeFormat(),
      });

      dateTimePeriod.value = [fromTime, toTime];
    }

    closeAll();
  };

  const onClickQuickPeriod = (selectedQuickItem: TimePeriodData) => {
    const { fromTime, toTime } = getCurrentTimeRange({
      period: selectedQuickItem.value,
      timeFormat: GLOBAL_TIME_LIVE_FORMAT,
      fixedTimes: getFixedTimeByTimeFormat(),
      direction: selectedQuickItem.direction ?? 'past',
    });

    applyRange(fromTime, toTime);
  };

  const onClickCalendarOk = ([from, to]: string[]) => {
    applyRange(from, to);
  };

  return {
    t,

    readonlyTimes,
    dateTimePeriod,
    selectedDateString,
    periodTagLabel,

    pastInputTime,
    pastInputError,
    pastInputUnit,
    filteredTimeUnitItems,
    onInputValidation,
    onClickInputTimeOk,

    recentlySearches,
    onClickRecentlySearch,
    deleteRecentlySearch,

    filteredQuickPeriods,
    onClickQuickPeriod,

    onClickCalendarOk,

    timeCalendarRef,
    dateTimePickerInputRef,
    dateTimePickerPopupRef,
    isShowQuickDatePopup,
    popupWrapperStyle,
    showCalendar,
    closeCalendar,
    openCalendar,
    closeAll,
    togglePopup,
  };
};
